/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import './styles.scss';
import React, { useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import UserSearch from '../search/UserSearch';
import { Loader } from '@g17eco/atoms/loader';
import { Suggestion } from '@g17eco/types/search';
import { OnboardingStatus } from '../../types/onboarding';
import { Avatar } from '@g17eco/atoms/avatar';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { DISABLED_DELEGATION_MESSAGE } from '@constants/user';

interface SearchModalProps {
  // Must be valid userId or email (onboarding)
  handleSubmit: (props: { userId: string; email?: string }) => void;
  toggle: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isOpen: boolean;
  title?: string;
  surveyId?: string;
  ignoredIds: string[];
}

export const SurveyDelegationSearchModal = (props: SearchModalProps) => {

  const { isOpen, title = 'Add a user' } = props;
  const [isSaving, setIsSaving] = useState(false);
  const [selection, setSelection] = useState<Suggestion | undefined>();

  const handleSubmit = () => {
    if (selection) {
      setIsSaving(true);
      const data =
        selection.status && [OnboardingStatus.NotStarted, OnboardingStatus.Pending].includes(selection.status)
          ? { userId: selection._id, email: selection.firstName } // firstName represents full email for onboarding user
          : { userId: selection._id };
      props.handleSubmit(data);
      setSelection(undefined);
      setIsSaving(false);
    }
  };

  const customRender = (suggestion: Suggestion) => {
    return (
      <SimpleTooltip text={suggestion.delegateDisabled ? DISABLED_DELEGATION_MESSAGE : ''}>
        <div className={`suggestion-item ${suggestion.delegateDisabled ? 'disabled' : ''}`}>
          <Avatar width='30px'>{suggestion.profile ? <img alt='avatar' src={suggestion.profile} /> : <></>}</Avatar>
          <div className='name'>
            <div>
              <strong>{suggestion.name}</strong>
            </div>
            <div>
              <small>{suggestion.jobTitle}</small>
            </div>
            <div className='dont_translate'>
              <small>{suggestion.email}</small>
            </div>
          </div>
        </div>
      </SimpleTooltip>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={props.toggle} backdrop='static' className='user-search-modal'>
      <ModalHeader toggle={props.toggle}>{title}</ModalHeader>
      <ModalBody>
        <p>Please type in a known user or e-mail address.</p>
        <div className='row mt-3'>
          <div className='offset-2 col-8 searchbox-container'>
            {isSaving ? (
              <Loader relative={true} />
            ) : (
              <UserSearch
                name={'stakeholder'}
                placeholder={'Name'}
                required={true}
                ignoredIds={props.ignoredIds}
                surveyId={props.surveyId}
                inputClassName={'form-control w-100'}
                hideOnboardings
                handleValueChange={(_searchStr, currentSuggestion) => {
                  if (currentSuggestion?.delegateDisabled) {
                    return;
                  }
                  setSelection(currentSuggestion);
                }}
                renderSuggestion={customRender}
              />
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button disabled={!selection || isSaving} onClick={handleSubmit}>
          Add User
        </Button>
      </ModalFooter>
    </Modal>
  );
}
